import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
  Disc3, ChevronRight, ChevronLeft, Loader2, Check, X,
  MapPin, Music, Users, ExternalLink, Pencil, ArrowRight,
  Instagram, Youtube, Music2, Mic2, Globe, Clock, Zap, GripVertical, Plus, Send,
  Library, BarChart2, Bookmark, Calendar, Bell, MessageCircle
} from "lucide-react";
import { DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import Header from "../components/Header";
import ImageUploadField from "../components/ImageUploadField";
import Footer from "../components/Footer";
import api from "../utils/api";
import { usePublicAuth } from "../context/PublicAuthContext";
import { toast } from "sonner";

// ─── Label-Link Benachrichtigungen ───────────────────────────────────────────
function LabelLinkNotifications({ token }) {
  const { t } = useTranslation();
  const [links, setLinks]     = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLinks = useCallback(async () => {
    try {
      const res = await api.get("/labels/artist/my-label-links", {
        headers: { Authorization: `Bearer ${token}` },
      });
      // Nur noch nicht bestätigte accepted-Links anzeigen (opt-out statt opt-in)
      setLinks((res.data || []).filter(l => l.status === "accepted" && !l.notified));
    } catch {}
    setLoading(false);
  }, [token]);

  useEffect(() => { if (token) fetchLinks(); }, [token, fetchLinks]);

  const respond = async (linkId, action) => {
    try {
      await api.put(`/labels/artist/my-label-links/${linkId}`, { action }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (action === "report") toast.success(t("labels.studio.notif_reported"));
      fetchLinks();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler");
    }
  };

  if (loading || links.length === 0) return null;

  return (
    <div className="mt-8 rounded-2xl overflow-hidden"
      style={{ background: "rgba(245,158,11,0.04)", border: "1px solid rgba(245,158,11,0.15)" }}>
      <div className="flex items-center gap-3 px-5 py-3.5 border-b" style={{ borderColor: "rgba(245,158,11,0.1)" }}>
        <Bell size={14} className="text-amber-400" />
        <p className="text-white font-bold text-sm">{t("labels.studio.notif_title")}</p>
        <span className="ml-auto px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/15 text-amber-400 border border-amber-500/20">
          {links.length} neu
        </span>
      </div>
      <div className="divide-y divide-white/[0.04]">
        {links.map(link => (
          <div key={link.link_id} className="flex items-center gap-4 px-5 py-3.5">
            <div className="flex-1 min-w-0">
              <a href={`/label/${link.label_slug}`} target="_blank" rel="noreferrer"
                className="text-sm font-semibold text-white hover:text-emerald-400 transition-colors truncate block">
                {link.label_name}
              </a>
              <p className="text-xs text-zinc-500 mt-0.5">{t("labels.studio.notif_added")}</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <button onClick={() => respond(link.link_id, "dismiss")} data-testid={`label-link-dismiss-${link.link_id}`}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-white/[0.06] hover:bg-white/10 text-zinc-300 transition-colors">
                <Check size={11} /> OK
              </button>
              <button onClick={() => respond(link.link_id, "report")} data-testid={`label-link-report-${link.link_id}`}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 transition-colors">
                <X size={11} /> Melden
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Sortierbare Track-Zeile ─────────────────────────────────────────────────
function SortableTrack({ track, index, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: track.id });
  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 }}
      className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900/40 group shadow-[inset_0_1px_0_rgba(255,255,255,0.02)]"
    >
      {/* Drag-Handle */}
      <button {...attributes} {...listeners} className="text-zinc-700 hover:text-zinc-400 transition cursor-grab active:cursor-grabbing p-1 touch-none flex-shrink-0">
        <GripVertical size={13} />
      </button>
      <span className="text-zinc-700 font-bold text-xs w-4 text-center flex-shrink-0">{index + 1}</span>
      {track.cover_url && <img src={track.cover_url} alt={track.titel} className="w-8 h-8 rounded-lg object-cover flex-shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">{track.interpret}</p>
        <p className="text-zinc-500 text-xs truncate">{track.titel}</p>
      </div>
      <button onClick={() => onRemove(track.id)}
        className="text-zinc-700 hover:text-red-400 transition p-1 rounded-lg opacity-0 group-hover:opacity-100 flex-shrink-0">
        <X size={12} />
      </button>
    </div>
  );
}

// ─── Artist-Bewerbungsformular ────────────────────────────────────────────────
function ArtistApplicationForm({ publicApi, compact = false }) {
  const { t } = useTranslation();
  const { publicUser } = usePublicAuth();
  const [artistName, setArtistName] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState(null);
  const [contactEmail, setContactEmail] = useState(publicUser?.email || "");
  const [infoText, setInfoText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!query.trim()) { setResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/artists/?search=${encodeURIComponent(query)}&limit=8`);
        setResults(r.data?.items || []);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const submit = async () => {
    if (!artistName.trim()) { setError(t("studio.app_error_name")); return; }
    if (!contactEmail.trim()) { setError(t("studio.app_error_email")); return; }
    if (!infoText.trim()) { setError(t("studio.app_error_info")); return; }
    setSubmitting(true); setError("");
    try {
      await publicApi("post", "/artist-applications", {
        artist_name: artistName.trim(),
        contact_email: contactEmail.trim(),
        info_text: infoText.trim(),
        existing_artist_id: selected?.id || null,
      });
      setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || t("studio.app_error_generic"));
    } finally { setSubmitting(false); }
  };

  const inputCls = "w-full bg-zinc-900 text-white rounded-xl px-4 py-2.5 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-blue-500/40 transition placeholder:text-zinc-600";

  if (done) return (
    <div className="text-center py-8">
      <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
        <Check size={20} className="text-emerald-400" />
      </div>
      <p className="text-white font-bold mb-1">{t("studio.app_submitted_title")}</p>
      <p className="text-zinc-500 text-sm">{t("studio.app_submitted_desc")}</p>
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Artist-Name */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
          {t("studio.artist_name_label")} <span className="text-rose-400">*</span>
        </label>
        <input
          value={artistName}
          onChange={e => setArtistName(e.target.value)}
          placeholder={t("studio.artist_name_placeholder")}
          className={inputCls}
        />
      </div>

      {/* Vorhandenen Artist verknüpfen (optional) */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
          {t("studio.archive_label")} <span className="text-zinc-700 font-normal normal-case">– {t("studio.archive_optional")}</span>
        </label>
        <div className="relative">
          <input
            value={query}
            onChange={e => { setQuery(e.target.value); if (!e.target.value) setSelected(null); }}
            placeholder={t("studio.archive_placeholder")}
            className={inputCls}
          />
          {selected && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 text-emerald-400 text-xs font-semibold">
              <Check size={12} /> {t("studio.linked_tag")}
            </div>
          )}
          {results.length > 0 && !selected && (
            <div className="absolute top-full mt-1 left-0 right-0 z-30 bg-zinc-950 border border-white/10 rounded-xl shadow-xl overflow-hidden">
              {results.map(a => (
                <button key={a.id} onClick={() => { setSelected(a); setQuery(a.name); setResults([]); }}
                  className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition text-left">
                  {a.bild_url
                    ? <img src={a.bild_url} alt={a.name} className="w-7 h-7 rounded-lg object-cover flex-shrink-0" />
                    : <div className="w-7 h-7 rounded-lg bg-zinc-800 flex items-center justify-center flex-shrink-0"><Mic2 size={11} className="text-zinc-600" /></div>
                  }
                  <span className="text-white text-sm">{a.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Kontakt-E-Mail */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
          {t("studio.application_email")} <span className="text-rose-400">*</span>
        </label>
        <input
          type="email"
          value={contactEmail}
          onChange={e => setContactEmail(e.target.value)}
          placeholder={t("studio.contact_email_ph")}
          className={inputCls}
        />
      </div>

      {/* Verifikation */}
      <div>
        <label className="block text-xs font-semibold uppercase tracking-wider text-zinc-500 mb-1.5">
          {t("studio.verify_label")} <span className="text-rose-400">*</span>
        </label>
        <textarea
          value={infoText}
          onChange={e => setInfoText(e.target.value.slice(0, 1000))}
          rows={4}
          placeholder={t("studio.verify_placeholder")}
          className={`${inputCls} resize-none`}
        />
        <p className="text-zinc-700 text-xs text-right mt-0.5">{infoText.length}/1000</p>
        <p className="text-zinc-600 text-xs mt-2 leading-relaxed">{t("studio.verify_hint")}</p>
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <button
        onClick={submit}
        disabled={submitting}
        className="w-full py-2.5 rounded-xl bg-blue-600 hover:bg-blue-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-sm transition flex items-center justify-center gap-2"
      >
        {submitting ? <Loader2 size={15} className="animate-spin" /> : <ArrowRight size={15} />}
        {t("studio.app_submit_btn")}
      </button>
    </div>
  );
}

// ─── Release einreichen Formular ─────────────────────────────────────────────
function ReleaseSubmitForm({ artistId, artistName, publicApi, onSuccess }) {
  const { t } = useTranslation();
  const RELEASE_TYPES = ["Single", "EP", "Album", "Compilation", "Remix"];
  const [form, setForm] = useState({
    interpret: artistName || "",
    titel: "",
    label: "",
    erscheinungsdatum: "",
    cover_url: "",
    typ: "Single",
    buy_links: { beatport: "", spotify: "", apple_music: "", amazon: "" },
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const set = (key, val) => setForm(p => ({ ...p, [key]: val }));
  const setBuy = (key, val) => setForm(p => ({ ...p, buy_links: { ...p.buy_links, [key]: val } }));

  const submit = async () => {
    if (!form.interpret.trim() || !form.titel.trim()) { setError(t("studio.release_error_required")); return; }
    setSubmitting(true); setError("");
    try {
      await publicApi("post", "/release-proposals/", {
        ...form,
        artist_id: artistId,
        buy_links: Object.fromEntries(Object.entries(form.buy_links).filter(([, v]) => v.trim())),
      });
      if (onSuccess) onSuccess();
      else setDone(true);
    } catch (e) {
      setError(e?.response?.data?.detail || t("studio.app_error_generic"));
    } finally { setSubmitting(false); }
  };

  if (done) return (
    <div className="text-center py-6">
      <div className="w-12 h-12 rounded-2xl bg-emerald-500/10 flex items-center justify-center mx-auto mb-3">
        <Check size={20} className="text-emerald-400" />
      </div>
      <p className="text-white font-bold mb-1">{t("studio.release_submitted_desc")}</p>
      <p className="text-zinc-500 text-sm">{t("studio.release_submitted_desc")}</p>
      <button onClick={() => setDone(false)} className="mt-4 text-xs text-rose-400 hover:text-rose-300 transition">
        {t("studio.release_submit_another")}
      </button>
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {/* Interpret */}
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_interpret")} *</label>
          <input value={form.interpret} onChange={e => set("interpret", e.target.value)}
            placeholder={t("studio.release_interpret_ph")} className="input-studio" />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_titel")} *</label>
          <input value={form.titel} onChange={e => set("titel", e.target.value)}
            placeholder={t("studio.release_titel_ph")} className="input-studio" />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_label")}</label>
          <input value={form.label} onChange={e => set("label", e.target.value)}
            placeholder={t("studio.release_label_ph")} className="input-studio" />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_type")}</label>
          <select value={form.typ} onChange={e => set("typ", e.target.value)} className="input-studio">
            {RELEASE_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_date")}</label>
          <input type="date" value={form.erscheinungsdatum} onChange={e => set("erscheinungsdatum", e.target.value)}
            className="input-studio" />
        </div>
        <div>
          <label className="block text-xs text-zinc-500 mb-1">{t("studio.release_cover")}</label>
          <input value={form.cover_url} onChange={e => set("cover_url", e.target.value)}
            placeholder="https://..." className="input-studio" />
        </div>
      </div>

      {/* Cover Vorschau */}
      {form.cover_url && (
        <div className="flex items-center gap-3">
          <img src={form.cover_url} alt="Cover" className="w-12 h-12 rounded-xl object-cover ring-1 ring-white/10"
            onError={e => e.target.style.display = "none"} />
          <span className="text-zinc-600 text-xs">{t("studio.release_cover_preview")}</span>
        </div>
      )}

      {/* Buy Links */}
      <div>
        <label className="block text-xs text-zinc-500 mb-2">{t("studio.release_buy_links")}</label>
        <div className="space-y-2">
          {[
            { key: "beatport", label: "Beatport" },
            { key: "spotify", label: "Spotify" },
            { key: "apple_music", label: "Apple Music" },
            { key: "amazon", label: "Amazon" },
          ].map(({ key, label }) => (
            <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "5.5rem 1fr" }}>
              <span className="text-zinc-600 text-xs text-right">{label}</span>
              <input value={form.buy_links[key]} onChange={e => setBuy(key, e.target.value)}
                placeholder="https://..." className="input-studio text-xs" />
            </div>
          ))}
        </div>
      </div>

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button onClick={submit} disabled={submitting}
        className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-bold transition">
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
        {submitting ? t("studio.release_submitting") : t("studio.release_submit_btn")}
      </button>
      <p className="text-zinc-700 text-xs">{t("studio.release_submit_hint")}</p>
    </div>
  );
}

// ─── Release einreichen Toggle ────────────────────────────────────────────────
const EQ_HEIGHTS = [38, 68, 52, 82, 47, 72, 44];

function ReleaseSubmitToggle({ artistId, artistName, publicApi }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (submitted) return (
    <div className="rounded-xl bg-zinc-900 px-5 py-8 text-center">
      <div className="w-10 h-10 rounded-xl bg-blue-500/15 flex items-center justify-center mx-auto mb-3">
        <Check size={18} className="text-blue-400" />
      </div>
      <p className="text-white font-bold text-sm mb-1">{t("studio.release_submitted_title")}</p>
      <p className="text-zinc-500 text-xs">{t("studio.release_submitted_desc")}</p>
      <button onClick={() => setSubmitted(false)} className="mt-4 text-xs text-zinc-600 hover:text-white transition">
        {t("studio.release_submit_another")}
      </button>
    </div>
  );

  if (!open) return (
    <button onClick={() => setOpen(true)}
      className="w-full group relative rounded-xl overflow-hidden text-left"
      style={{ background: "linear-gradient(110deg, #0c1a2e 0%, #0f2241 55%, #0c1a2e 100%)", boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.3) inset, 0 4px 16px rgba(0,0,0,0.4)" }}
    >
      <div className="absolute right-5 inset-y-0 flex items-end gap-[3px] py-3 opacity-[0.18] group-hover:opacity-[0.35] transition-opacity duration-300">
        {EQ_HEIGHTS.map((h, i) => (<div key={i} className="w-[3px] rounded-full bg-blue-400" style={{ height: `${h}%` }} />))}
      </div>
      <div className="relative flex items-center gap-4 px-5 py-4 pr-20">
        <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: "rgba(59,130,246,0.18)", boxShadow: "inset 0 1px 0 rgba(147,197,253,0.12)" }}>
          <Disc3 size={16} className="text-blue-400" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-white font-bold text-sm leading-tight">{t("studio.release_submit_title")}</p>
          <p className="text-zinc-500 text-xs mt-0.5">{t("studio.release_toggle_desc")}</p>
        </div>
        <Plus size={14} className="text-zinc-700 group-hover:text-blue-400 transition-colors flex-shrink-0" />
      </div>
    </button>
  );

  return (
    <div className="rounded-xl overflow-hidden"
      style={{ background: "linear-gradient(180deg, #0c1a2e 0%, #0a0f1a 50%, #080808 100%)", boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 4px 24px rgba(0,0,0,0.5)" }}
    >
      <div className="flex items-center justify-between px-5 py-3.5"
        style={{ borderBottom: "1px solid rgba(59,130,246,0.12)" }}>
        <div className="flex items-center gap-3">
          <Disc3 size={14} className="text-blue-400" />
          <p className="text-white font-bold text-sm">{t("studio.release_submit_title")}</p>
          {artistName && <span className="text-zinc-600 text-xs">· {artistName}</span>}
        </div>
        <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-zinc-600 hover:text-white hover:bg-white/[0.04] transition">
          <X size={14} />
        </button>
      </div>
      <div className="px-5 py-5">
        <ReleaseSubmitForm artistId={artistId} artistName={artistName} publicApi={publicApi}
          onSuccess={() => { setOpen(false); setSubmitted(true); }} />
      </div>
    </div>
  );
}




function EditSection({ title, badge, children, onSave, saving, instant = false }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className={`rounded-2xl border transition-all duration-300 ${open
      ? "border-rose-500/30 bg-rose-500/[0.03]"
      : "border-white/[0.05] bg-zinc-900/30 hover:border-white/10"}`}>
      <div className="flex items-center justify-between px-5 py-4">
        <div className="flex items-center gap-3">
          <span className="text-white font-semibold text-sm">{title}</span>
          {badge && (
            <span className="text-[10px] px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 font-medium">
              {badge}
            </span>
          )}
        </div>
        <button
          onClick={() => setOpen(v => !v)}
          disabled={false}
          className={`text-xs px-3 py-1.5 rounded-xl font-semibold transition flex items-center gap-1.5 ${
            open
              ? "bg-zinc-800 text-zinc-400 hover:text-white"
              : "bg-white/[0.06] text-zinc-400 hover:bg-white/10 hover:text-white"
          }`}
        >
          {open ? <><X size={11} /> {t("studio.edit_cancel")}</> : <><Pencil size={11} /> {t("studio.edit_change_btn")}</>}
        </button>
      </div>
      {open && (
        <div className="px-5 pb-5 space-y-4">
          <div className="h-px bg-white/[0.05]" />
          {children}
          <button
            onClick={async () => { await onSave(); setOpen(false); }}
            disabled={saving}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-rose-600 hover:bg-rose-500 disabled:opacity-50 text-white text-sm font-bold transition"
          >
            {saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
            {saving ? t("studio.edit_saving") : instant ? t("studio.edit_save") : t("studio.edit_submit")}
          </button>
          {!instant && (
            <p className="text-zinc-700 text-xs">{t("studio.edit_approval_hint")}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Edit-View für einen Artist ──────────────────────────────────────────────
function ArtistEditView({ artistId, artistsMap, publicApi, onBack, onArtistUpdated }) {
  const { t } = useTranslation();
  const artist = artistsMap[artistId];
  const [bio, setBio] = useState(artist?.biografie || "");
  const [bildUrl, setBildUrl] = useState(artist?.bild_url || "");
  const [bannerUrl, setBannerUrl] = useState(artist?.banner_url || "");
  const [wohnort, setWohnort] = useState(artist?.wohnort || "");
  const [social, setSocial] = useState(artist?.social_links || {});
  const [topTracks, setTopTracks] = useState([]);
  const [trackSearch, setTrackSearch] = useState("");
  const [trackResults, setTrackResults] = useState([]);
  const [sectionSaving, setSectionSaving] = useState({});
  const [sectionSaved, setSectionSaved] = useState({});
  const [pendingBio, setPendingBio] = useState(artist?.pending_bio || null);
  const [pendingChanges, setPendingChanges] = useState(artist?.pending_changes || null);

  useEffect(() => {
    if (!artist) return;
    setBio(artist.biografie || "");
    setBildUrl(artist.bild_url || "");
    setBannerUrl(artist.banner_url || "");
    setWohnort(artist.wohnort || "");
    setSocial(artist.social_links || {});
    setPendingBio(artist.pending_bio || null);
    setPendingChanges(artist.pending_changes || null);
    if (artist.top_track_ids?.length) {
      Promise.all(artist.top_track_ids.map(id => api.get(`/releases/by-id/${id}`).then(x => x.data).catch(() => null)))
        .then(t => setTopTracks(t.filter(Boolean)));
    }
  }, [artistId, artist]);

  useEffect(() => {
    if (!trackSearch.trim()) { setTrackResults([]); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.get(`/releases/?search=${encodeURIComponent(trackSearch)}&limit=8`);
        setTrackResults(r.data?.items || []);
      } catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [trackSearch]);

  const saveSection = async (section, payload) => {
    setSectionSaving(p => ({ ...p, [section]: true }));
    try {
      const res = await publicApi("put", "/me/artist-profile", {
        target_artist_id: artistId,
        ...payload,
      });
      if (res.data?.bio_pending_review) setPendingBio(res.data.pending_bio);
      if (res.data?.changes_pending_review) setPendingChanges(true);
      setSectionSaved(p => ({ ...p, [section]: true }));
      setTimeout(() => setSectionSaved(p => ({ ...p, [section]: false })), 3000);
      onArtistUpdated?.(artistId);
    } catch {} finally {
      setSectionSaving(p => ({ ...p, [section]: false }));
    }
  };

  const dndSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor)
  );

  const handleTrackDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setTopTracks(prev => {
      const oldIdx = prev.findIndex(t => t.id === active.id);
      const newIdx = prev.findIndex(t => t.id === over.id);
      return arrayMove(prev, oldIdx, newIdx);
    });
  };

  const SOCIAL_FIELDS = [
    { key: "instagram", label: "Instagram" },
    { key: "facebook", label: "Facebook" },
    { key: "soundcloud", label: "SoundCloud" },
    { key: "beatport", label: "Beatport" },
    { key: "spotify", label: "Spotify" },
    { key: "youtube", label: "YouTube" },
    { key: "tiktok", label: "TikTok" },
    { key: "twitch", label: "Twitch" },
  ];

  return (
    <div>
      {/* Back */}
      <button onClick={onBack} className="flex items-center gap-2 text-zinc-500 hover:text-white text-sm mb-6 transition group">
        <ChevronLeft size={16} className="group-hover:-translate-x-0.5 transition-transform" />
        {t("studio.back_to_studio")}
      </button>

      {/* Artist Header */}
      <div className="flex items-center gap-5 mb-8 pb-6 border-b border-white/[0.06]">
        {artist?.bild_url
          ? <img src={artist.bild_url} alt={artist.name} className="w-16 h-16 rounded-2xl object-cover ring-1 ring-white/10 flex-shrink-0" />
          : <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center flex-shrink-0"><Disc3 size={24} className="text-blue-400" /></div>
        }
        <div>
          <h2 className="text-2xl font-black text-white">{artist?.name}</h2>
          <Link to={`/artist/${artist?.slug}`} target="_blank" className="text-xs text-zinc-500 hover:text-rose-400 transition flex items-center gap-1 mt-0.5">
            {t("studio.view_profile")} <ExternalLink size={10} />
          </Link>
        </div>
      </div>

      <div className="space-y-3">
        {/* Bio */}
        <EditSection
          title="Artist Bio"
          badge={pendingBio ? t("studio.pending_approval") : null}
          onSave={() => saveSection("bio", { bio })}
          saving={sectionSaving.bio}
          saved={sectionSaved.bio}
        >
          <textarea
            value={bio}
            onChange={e => setBio(e.target.value.slice(0, 5000))}
            rows={6}
            placeholder={t("studio.bio_placeholder")}
            className="w-full bg-zinc-900/60 text-white rounded-2xl px-4 py-3 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700 resize-y min-h-[120px]"          />
          <p className="text-zinc-700 text-xs text-right">{bio.length}/5000</p>
        </EditSection>

        {/* Profilbild */}
        <EditSection
          title={t("studio.profile_image")}
          badge={pendingChanges?.bild_url ? t("studio.pending_approval") : null}
          onSave={() => saveSection("bild", { bild_url: bildUrl || null })}
          saving={sectionSaving.bild}
          saved={sectionSaved.bild}
        >
          <ImageUploadField
            value={bildUrl}
            onChange={setBildUrl}
            uploadEndpoint="/media/artist-image"
            oldPendingUrl={pendingChanges?.bild_url || ""}
            label={t("studio.profile_image")}
            testId="artist-bild-studio"
          />
        </EditSection>

        {/* Banner */}
        <EditSection
          title="Banner"
          onSave={() => saveSection("banner", { banner_url: bannerUrl || null })}
          saving={sectionSaving.banner}
          saved={sectionSaved.banner}
        >
          <p className="text-zinc-600 text-xs mb-3">
            Empfehlung: <span className="text-zinc-400 font-semibold">1400 × 400 px</span> &nbsp;·&nbsp; breit und flach (ca. 3.5:1) &nbsp;·&nbsp; max. 8 MB &nbsp;·&nbsp; JPEG, PNG oder WebP
          </p>
          <ImageUploadField
            value={bannerUrl}
            onChange={setBannerUrl}
            uploadEndpoint="/media/artist-banner"
            oldPendingUrl=""
            label="Banner"
            testId="artist-banner-studio"
            variant="banner"
          />
        </EditSection>

        {/* Wohnort */}
        <EditSection
          title={t("studio.location")}
          badge={pendingChanges?.wohnort ? t("studio.pending_approval") : null}
          onSave={() => saveSection("wohnort", { wohnort: wohnort || null })}
          saving={sectionSaving.wohnort}
          saved={sectionSaved.wohnort}
        >
          <input
            value={wohnort}
            onChange={e => setWohnort(e.target.value)}
            placeholder="z.B. Berlin, Deutschland"
            className="w-full bg-zinc-900/60 text-white rounded-2xl px-4 py-3 text-sm outline-none ring-1 ring-inset ring-white/[0.07] focus:ring-rose-500/40 transition placeholder:text-zinc-700"
          />
        </EditSection>

        {/* Social Links */}
        <EditSection
          title={t("studio.social_links_title")}
          badge={pendingChanges?.social_links ? t("studio.pending_approval") : null}
          onSave={() => saveSection("social", { social_links: Object.keys(social).length ? social : null })}
          saving={sectionSaving.social}
          saved={sectionSaved.social}
        >
          <div className="space-y-2">
            {SOCIAL_FIELDS.map(({ key, label }) => (
              <div key={key} className="grid items-center gap-2" style={{ gridTemplateColumns: "6rem 1fr" }}>
                <span className="text-zinc-500 text-xs font-medium text-right">{label}</span>
                <input
                  value={social[key] || ""}
                  onChange={e => setSocial(p => ({ ...p, [key]: e.target.value }))}
                  placeholder={`https://${key}.com/...`}
                  className="bg-zinc-900/60 text-white rounded-xl px-3 py-2 text-xs outline-none ring-1 ring-inset ring-white/[0.06] focus:ring-rose-500/40 transition placeholder:text-zinc-700"
                />
              </div>
            ))}
          </div>
        </EditSection>

        {/* Top 5 Tracks */}
        <EditSection
          title={t("studio.top5_title", { count: topTracks.length })}
          onSave={() => saveSection("tracks", { top_track_ids: topTracks.map(t => t.id) })}
          saving={sectionSaving.tracks}
          saved={sectionSaved.tracks}
          instant
        >
          <div className="space-y-2 mb-3">
            <DndContext
              sensors={dndSensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis]}
              onDragEnd={handleTrackDragEnd}
            >
              <SortableContext items={topTracks.map(t => t.id)} strategy={verticalListSortingStrategy}>
                {topTracks.map((t, i) => (
                  <SortableTrack
                    key={t.id}
                    track={t}
                    index={i}
                    onRemove={(id) => setTopTracks(p => p.filter(x => x.id !== id))}
                  />
                ))}
              </SortableContext>
            </DndContext>
            {topTracks.length < 5 && (
              <div className="relative">
                <input
                  value={trackSearch}
                  onChange={e => setTrackSearch(e.target.value)}
                  placeholder={t("studio.track_search_ph")}
                  className="w-full bg-zinc-900/30 rounded-2xl px-4 py-3 text-sm text-white outline-none ring-1 ring-inset ring-dashed ring-white/[0.08] focus:ring-rose-500/30 transition placeholder:text-zinc-700"
                />
                {trackResults.length > 0 && (
                  <div className="absolute top-full mt-2 left-0 right-0 z-50 bg-zinc-950/95 border border-white/10 rounded-2xl shadow-2xl max-h-64 overflow-y-auto">
                    {trackResults.map(r => (
                      <button key={r.id} onClick={() => { setTopTracks(p => [...p, r]); setTrackSearch(""); setTrackResults([]); }}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition text-left">
                        {r.cover_url && <img src={r.cover_url} alt={r.titel} className="w-8 h-8 rounded-lg object-cover" />}
                        <div className="min-w-0">
                          <p className="text-white text-sm truncate">{r.interpret}</p>
                          <p className="text-zinc-500 text-xs truncate">{r.titel}</p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </EditSection>
      </div>

      {/* ── Release einreichen ── */}
      <div className="mt-6 pt-6 border-t border-white/[0.05]">
        <ReleaseSubmitToggle artistId={artistId} artistName={artist?.name} publicApi={publicApi} />
      </div>
    </div>
  );
}
function ArtistCard({ artistId, artistData, isPrimary, onEdit }) {
  const { t } = useTranslation();
  const pending = artistData?.pending_bio || artistData?.pending_changes;

  const stats = [
    { label: "Releases",     value: artistData?.releases_count     ?? 0, icon: Disc3 },
    { label: "Remixes",      value: artistData?.remixes_count      ?? 0, icon: Music2 },
    { label: "Compilations", value: artistData?.compilations_count ?? 0, icon: Library },
    { label: "Follower",     value: artistData?.followers_count    ?? 0, icon: Users },
    { label: "Votes",        value: artistData?.total_votes        ?? 0, icon: BarChart2 },
    { label: "Saves",        value: artistData?.saved_count        ?? 0, icon: Bookmark },
  ];

  const popularityScore = Math.round(
    ((artistData?.total_votes || 0) * 3) +
    ((artistData?.followers_count || 0) * 5) +
    ((artistData?.saved_count || 0) * 2)
  );

  const birthday = artistData?.geburtstag
    ? new Date(artistData.geburtstag).toLocaleDateString("de", { day: "numeric", month: "long", year: "numeric" })
    : null;

  return (
    <div
      className="rounded-2xl overflow-hidden flex flex-col sm:flex-row"
      style={{
        background: "linear-gradient(160deg, #0c1a2e 0%, #0f2241 60%, #080f1c 100%)",
        boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 8px 32px rgba(0,0,0,0.5)",
        border: "1px solid rgba(59,130,246,0.12)",
      }}
    >
      {/* ── Bild links ── */}
      <div className="relative w-full sm:w-52 h-52 sm:h-auto flex-shrink-0 overflow-hidden">
        {artistData?.bild_url
          ? <img src={artistData.bild_url} alt={artistData.name}
              className="w-full h-full object-cover object-top" />
          : <div className="w-full h-full flex items-center justify-center"
              style={{ background: "rgba(8,15,28,0.8)" }}>
              <Disc3 size={40} className="text-blue-900/50" />
            </div>
        }
        {/* Mobile: Gradient nach unten */}
        <div className="absolute inset-0 sm:hidden"
          style={{ background: "linear-gradient(to bottom, transparent 50%, rgba(8,15,28,1) 100%)" }} />
        {/* Desktop: Gradient nach rechts */}
        <div className="hidden sm:block absolute inset-0"
          style={{ background: "linear-gradient(to right, transparent 65%, rgba(8,15,28,0.9) 100%)" }} />

        {/* Equalizer-Deko */}
        <div className="hidden sm:flex absolute right-0 inset-y-0 items-end gap-[3px] pr-1 py-5 opacity-10">
          {[55,80,40,95,65,45,70,50].map((h, i) => (
            <div key={i} className="w-[3px] rounded-full bg-blue-400" style={{ height: `${h}%` }} />
          ))}
        </div>
      </div>

      {/* ── Info rechts ── */}
      <div className="flex-1 flex flex-col p-5 -mt-8 sm:mt-0 relative z-10">

        {/* Badges + Name + Score */}
        <div className="flex items-start justify-between gap-2 mb-1">
          <div className="flex-1">
            <div className="flex items-center gap-1.5 flex-wrap mb-1.5">
              {isPrimary && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white"
                  style={{ background: "rgba(59,130,246,0.6)" }}>
                  {t("studio.artist_primary_badge")}
                </span>
              )}
              {pending && (
                <span className="text-[10px] px-2 py-0.5 rounded-full font-bold text-white"
                  style={{ background: "rgba(234,179,8,0.6)" }}>
                  {t("studio.artist_pending_badge")}
                </span>
              )}
            </div>
            <h3 className="text-white font-black text-xl leading-tight">
              {artistData?.name || artistId.slice(-6)}
            </h3>
          </div>
          {popularityScore > 0 && (
            <div className="flex items-center gap-1 text-[10px] font-bold text-blue-400/50 flex-shrink-0 mt-1">
              <Zap size={9} /> {popularityScore.toLocaleString("de")}
            </div>
          )}
        </div>

        {/* Meta: Heimatstadt + Geburtstag */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mb-4">
          {artistData?.wohnort && (
            <p className="text-zinc-500 text-xs flex items-center gap-1">
              <MapPin size={9} className="text-blue-500/40" /> {artistData.wohnort}
            </p>
          )}
          {birthday && (
            <p className="text-zinc-500 text-xs flex items-center gap-1">
              <Calendar size={9} className="text-blue-500/40" /> {birthday}
            </p>
          )}
        </div>

        {/* Stats 3×2 */}
        <div className="grid grid-cols-3 gap-px rounded-xl overflow-hidden mb-4"
          style={{ background: "rgba(59,130,246,0.07)" }}>
          {stats.map(({ label, value, icon: Icon }) => (
            <div key={label} className="flex flex-col items-center justify-center py-2.5 gap-0.5"
              style={{ background: "rgba(8,15,28,0.85)" }}>
              <span className="text-white font-black text-base leading-none">{value.toLocaleString("de")}</span>
              <span className="text-zinc-600 text-[9px] uppercase tracking-wider flex items-center gap-0.5">
                <Icon size={7} className="text-blue-500/40" /> {label}
              </span>
            </div>
          ))}
        </div>

        {/* Buttons */}
        <div className="flex gap-2 mt-auto">
          {artistData?.slug && (
            <a
              href={`/artist/${artistData.slug}`}
              target="_blank"
              rel="noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs text-zinc-500 hover:text-white transition-colors"
              style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)" }}
            >
              <ExternalLink size={11} /> Profil
            </a>
          )}
          <button
            onClick={() => onEdit(artistId)}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-xl text-xs font-bold text-white transition-all hover:brightness-110"
            style={{
              background: "linear-gradient(110deg, rgba(59,130,246,0.25) 0%, rgba(59,130,246,0.12) 100%)",
              border: "1px solid rgba(59,130,246,0.25)",
              boxShadow: "0 1px 0 rgba(147,197,253,0.08) inset",
            }}
          >
            <Pencil size={11} /> Infos Bearbeiten
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Weiteren Artist anfragen (für bereits verknüpfte User) ──────────────────
function AdditionalArtistToggle({ publicApi }) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-4">
      {!open ? (
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 text-zinc-600 hover:text-zinc-400 text-xs transition"
        >
          <Plus size={13} /> {t("studio.additional_artist_btn")}
        </button>
      ) : (
        <div className="rounded-xl border border-white/[0.06] bg-zinc-900/30 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-white/[0.05]">
            <p className="text-white font-bold text-sm">{t("studio.additional_artist_btn")}</p>
            <button onClick={() => setOpen(false)} className="p-1.5 rounded-lg text-zinc-600 hover:text-white hover:bg-white/[0.04] transition">
              <X size={14} />
            </button>
          </div>
          <div className="p-5">
            <ArtistApplicationForm publicApi={publicApi} compact />
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Haupt-Seite ─────────────────────────────────────────────────────────────
export default function ArtistStudioPage() {
  const { t } = useTranslation();
  const { isPublicAuth, publicUser, publicApi, token, isPublicLoading } = usePublicAuth();
  const navigate = useNavigate();
  const [artistsMap, setArtistsMap] = useState({});
  const [loadingArtists, setLoadingArtists] = useState(false);
  const [editingArtistId, setEditingArtistId] = useState(null);
  const [sortedIds, setSortedIds] = useState([]);

  const artistIds = publicUser?.artist_ids || (publicUser?.artist_id ? [publicUser.artist_id] : []);
  const isLinked = artistIds.length > 0;

  useEffect(() => {
    if (!isPublicLoading && !isPublicAuth) navigate("/login");
  }, [isPublicAuth, isPublicLoading, navigate]);

  useEffect(() => {
    if (!artistIds.length) return;
    setSortedIds(artistIds);
    setLoadingArtists(true);
    Promise.all(
      artistIds.map(id => api.get(`/artists/${id}`).then(r => [id, r.data]).catch(() => null))
    ).then(results => {
      const map = {};
      results.filter(Boolean).forEach(([id, data]) => { map[id] = data; });
      setArtistsMap(map);
    }).finally(() => setLoadingArtists(false));
  }, [artistIds.join(",")]); // eslint-disable-line

  const refreshArtist = useCallback(async (id) => {
    try {
      const r = await api.get(`/artists/${id}`);
      setArtistsMap(p => ({ ...p, [id]: r.data }));
    } catch {}
  }, []);

  if (isPublicLoading) return (
    <div className="min-h-screen bg-[#060606] flex items-center justify-center">
      <Loader2 size={24} className="animate-spin text-zinc-600" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#060606] text-white">
      <Helmet>
        <title>Artist Studio | HUWND</title>
        <meta name="robots" content="noindex" />
      </Helmet>
      <Header />

      {/* Hero-Streifen */}
      <div className="relative border-b border-white/[0.04] overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-0 left-1/4 w-96 h-32 bg-blue-600/6 rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center flex-shrink-0">
              <Disc3 size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-400/60 mb-0.5">Artist</p>
              <h1 className="text-3xl font-black text-white tracking-tight">Studio</h1>
            </div>
            {isLinked && editingArtistId === null && (
              <div className="ml-auto flex items-center gap-3">
                {/* Link zum öffentlichen Artist-Profil */}
                {publicUser?.artist_slug && (
                  <Link
                    to={`/artist/${publicUser.artist_slug}`}
                    data-testid="studio-view-public-profile"
                    className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                  >
                    <ExternalLink size={11} /> Profil ansehen
                  </Link>
                )}
                <span className="hidden sm:flex items-center gap-1.5 text-xs text-zinc-600">
                  <Zap size={12} className="text-blue-500/50" />
                  {t("studio.linked_count", { count: artistIds.length })}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10">

        {/* ── Nicht verknüpft: Marketing + Bewerbung ── */}
        {!isLinked && (
          <div className="max-w-2xl mx-auto">

            {/* Hero */}
            <div className="space-y-4 mb-10">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{ background: "rgba(59,130,246,0.1)", border: "1px solid rgba(59,130,246,0.2)", color: "#60a5fa" }}>
                <Disc3 size={11} /> Artist Studio
              </div>
              <h2 className="text-4xl sm:text-5xl font-black text-white tracking-tight leading-tight">
                Dein Artist-Profil.<br />
                <span className="text-transparent bg-clip-text" style={{ backgroundImage: "linear-gradient(135deg, #60a5fa, #2563eb)" }}>
                  Deine Präsenz.
                </span>
              </h2>
              <p className="text-zinc-400 text-base leading-relaxed max-w-xl">
                Du bist Artist oder DJ in der Hands-Up Community? Erstelle dein offizielles Profil auf HUWND
                und verwalte deinen Auftritt direkt im Studio.
              </p>
            </div>

            {/* Feature Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-10">
              {[
                { icon: Disc3,     title: "Artist-Profil",      desc: "Bio, Profilbild, Wohnort und Social Links auf einer öffentlichen Profilseite." },
                { icon: Music2,    title: "Persönliche Top 5",  desc: "Präsentiere deine 5 stärksten Releases auf deinem Artist-Profil." },
                { icon: Pencil,    title: "Studio-Verwaltung",  desc: "Alle Infos jederzeit aktualisieren – Änderungen werden nach Freigabe sichtbar." },
                { icon: Users,     title: "Artist-Badge",       desc: "Blauer Verified-Ring und Artist-Badge in Kommentaren und der Community." },
                { icon: BarChart2, title: "Stats & Releases",   desc: "Releases, Remixes, Follower und Votes – alles auf einen Blick." },
                { icon: Send,      title: "Release einreichen", desc: "Reiche neue Releases direkt aus dem Artist Studio zur Veröffentlichung ein." },
              ].map(f => (
                <div key={f.title} className="p-4 rounded-2xl bg-zinc-900/60 border border-white/[0.06] flex items-start gap-3">
                  <div className="w-8 h-8 rounded-xl bg-zinc-800 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <f.icon size={14} className="text-blue-400" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-white">{f.title}</p>
                    <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{f.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* So funktioniert es */}
            <div className="space-y-4 mb-10">
              <p className="text-xs font-bold text-zinc-500 uppercase tracking-widest">So funktioniert es</p>
              <div className="space-y-3">
                {[
                  { n: "01", t: "Bewerbung einreichen", d: "Fülle das Formular unten aus. Du kannst deinen bestehenden Artist-Eintrag in unserer Datenbank direkt verknüpfen." },
                  { n: "02", t: "Manuelle Prüfung",     d: "Wir prüfen jede Bewerbung sorgfältig und bestätigen deine Identität als Artist." },
                  { n: "03", t: "Studio-Zugang",        d: "Nach Genehmigung hast du vollen Zugriff auf dein Artist Studio zur Profilpflege." },
                ].map(s => (
                  <div key={s.n} className="flex gap-4">
                    <span className="text-2xl font-black text-white/10 font-mono w-10 flex-shrink-0">{s.n}</span>
                    <div>
                      <p className="text-sm font-bold text-white">{s.t}</p>
                      <p className="text-xs text-zinc-500 mt-0.5 leading-relaxed">{s.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Formular – Dark Gradient Card (blau) */}
            <div className="rounded-2xl overflow-hidden" style={{
              background: "linear-gradient(160deg, #0c1a2e 0%, #0f2241 60%, #080f1c 100%)",
              boxShadow: "0 2px 0 rgba(255,255,255,0.04) inset, 0 -1px 0 rgba(0,0,0,0.4) inset, 0 16px 48px rgba(0,0,0,0.6)",
              border: "1px solid rgba(59,130,246,0.18)",
            }}>
              <div className="flex items-center gap-4 px-6 py-5 border-b" style={{ borderColor: "rgba(59,130,246,0.10)" }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.18)" }}>
                  <Disc3 size={18} className="text-blue-400" />
                </div>
                <div>
                  <p className="font-bold text-white text-base">Artist-Zugang beantragen</p>
                  <p className="text-sm mt-0.5" style={{ color: "rgba(255,255,255,0.4)" }}>
                    Bewirb dich jetzt – Bearbeitung dauert in der Regel 1–3 Werktage.
                  </p>
                </div>
              </div>
              <div className="p-6">
                <ArtistApplicationForm publicApi={publicApi} />
              </div>
            </div>
          </div>
        )}

        {/* ── Verknüpft: Edit-View ── */}
        {isLinked && editingArtistId && (
          <ArtistEditView
            artistId={editingArtistId}
            artistsMap={artistsMap}
            publicApi={publicApi}
            onBack={() => setEditingArtistId(null)}
            onArtistUpdated={refreshArtist}
          />
        )}

        {/* ── Verknüpft: Home/Dashboard ── */}
        {isLinked && !editingArtistId && (
          <div>
            {/* Intro-Text */}
            <div className="mb-8">
              <h2 className="text-xl font-black text-white mb-1">{t("studio.your_artists_title")}</h2>
              <p className="text-zinc-600 text-sm">{t("studio.your_artists_desc")}</p>
            </div>

            {loadingArtists ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5">
                {artistIds.map(id => (
                  <div key={id} className="rounded-3xl bg-zinc-900/40 border border-white/[0.05] overflow-hidden">
                    <div className="aspect-[4/3] bg-zinc-800 animate-pulse" />
                    <div className="p-4 space-y-2">
                      <div className="h-5 bg-zinc-800 animate-pulse rounded-lg w-3/4" />
                      <div className="h-3 bg-zinc-800 animate-pulse rounded-lg w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col gap-5 max-w-2xl">
                {sortedIds.map((id, idx) => (
                  <ArtistCard
                    key={id}
                    artistId={id}
                    artistData={artistsMap[id]}
                    isPrimary={idx === 0 && sortedIds.length > 1}
                    onEdit={setEditingArtistId}
                  />
                ))}
              </div>
            )}

            {/* ── Collab Finder Card ── */}
            <div className="mt-4">
              <Link
                to="/studio/collabs"
                data-testid="studio-collab-finder-link"
                className="flex items-center gap-4 px-5 py-4 rounded-2xl transition-all group"
                style={{
                  background: "linear-gradient(110deg, rgba(59,130,246,0.08) 0%, rgba(139,92,246,0.05) 100%)",
                  border: "1px solid rgba(59,130,246,0.12)",
                }}
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 group-hover:scale-105 transition-transform"
                  style={{ background: "rgba(59,130,246,0.12)", border: "1px solid rgba(59,130,246,0.2)" }}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-5 h-5 text-blue-400"
                    strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/>
                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                  </svg>
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-bold text-sm">{t("collab.nav_title")}</p>
                    <span className="text-[10px] px-2 py-0.5 rounded-full font-bold"
                      style={{ background: "rgba(59,130,246,0.15)", color: "#60a5fa", border: "1px solid rgba(59,130,246,0.2)" }}>
                      {t("collab.nav_badge")}
                    </span>
                  </div>
                  <p className="text-zinc-500 text-xs mt-0.5">{t("collab.nav_desc")}</p>
                </div>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="w-4 h-4 text-zinc-600 group-hover:text-zinc-400 transition-colors" strokeLinecap="round">
                  <path d="M9 18l6-6-6-6"/>
                </svg>
              </Link>
            </div>

            {/* ── Release einreichen (Dashboard-Karte) ── */}
            <div className="mt-8">
              <ReleaseSubmitToggle
                artistId={sortedIds[0]}
                artistName={artistsMap[sortedIds[0]]?.name}
                publicApi={publicApi}
              />
            </div>

            {/* ── Label-Link Benachrichtigungen ── */}
            <LabelLinkNotifications token={token} />

            {/* Hinweis-Box */}
            <div className="mt-8 p-5 rounded-2xl bg-zinc-900/30 border border-white/[0.04]">
              <div className="flex items-start gap-3">
                <Clock size={14} className="text-zinc-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-zinc-400 text-sm font-semibold mb-0.5">{t("studio.approval_title")}</p>
                  <p className="text-zinc-600 text-xs leading-relaxed">{t("studio.approval_desc")}</p>
                </div>
              </div>
            </div>

            {/* Weiteren Artist anfragen */}
            <AdditionalArtistToggle publicApi={publicApi} />

            {/* Feedback-Hinweis */}
            <div className="mt-6 flex items-start gap-3 px-4 py-3.5 rounded-xl bg-zinc-900/20 border border-white/[0.03]">
              <MessageCircle size={13} className="text-zinc-600 mt-0.5 flex-shrink-0" />
              <p className="text-zinc-600 text-xs leading-relaxed">{t("studio.feedback_note")}</p>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
